import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, fadeInLeft, fadeInRight } from '../../animations/animations';
import { MdWork, MdLocationOn, MdCalendarToday } from 'react-icons/md';

const experiences = [
  {
    company: 'Probots Electronics India Pvt Ltd',
    location: 'Bengaluru, India',
    role: 'Embedded Engineer - II (Team Lead)',
    period: 'Apr 2024 – Apr 2026',
    highlights: [
      'Team Leadership: Owned the entire firmware stack end-to-end, from rootfs and cross-compiler toolchain to application firmware and APIs. Led sprint planning, defined task breakdowns and timelines, and maintained final sign-off on all production releases. Reviewed all team PRs. Represented engineering directly on customer calls and acted as the primary technical liaison to US-based clients. Wrote and maintained company-wide coding standards and mentored junior engineers on embedded best practices.',
      'Firmware Development (C/Linux): Tuned thread stack sizes and heap allocation patterns across the firmware to reduce per-thread memory overhead and eliminate fragmentation under continuous operation. Wrote and maintained the main control firmware for a fleet of ASIC-based BTC mining devices, managing IPC and enforcing thermal limits on CVITEK/AMLOGIC multicore platforms to sustain 99.9% uptime.',
      'Secure OTA Firmware Updater: Built a production OTA update system in C for AMLOGIC and CVITEK platforms. Implemented streaming AES-256-CBC decryption and RSA-2048 signature verification before any files touched the target filesystem. Added downgrade protection with SHA-256 hashed secret override, pre-flight disk space checks, and platform-specific post-install handling, all driven by a structured exit-code protocol consumed by the calling orchestration layer.',
      'Firmware Reverse Engineering: Reverse engineered a proprietary OEM BTC miner firmware stack with no vendor documentation. Used a logic analyzer to capture and decode UART and I2C signal traffic, Wireshark to map the TCP/network control protocol, and Ghidra to analyze OEM firmware binaries, extracting the communication logic needed to build compatible replacement control firmware from scratch.',
      'BSP & Low-Level Development: Configured U-Boot bootloader, compiled custom Linux images from source, and managed kernel headers for ARM targets. Implemented I2C bit-banging in C for peripherals where hardware I2C was unavailable. Worked around a locked CVITEK bootloader by mounting a custom rootfs using Linux namespaces via unshare, running the full firmware stack as a namespaced process on top of the OEM environment without touching vendor bootloader configs.',
      'Toolchain & Build Systems: Built a custom ARM cross-compiler toolchain using crosstool-ng to target a locked-down CVITEK platform where no vendor toolchain existed and standard compilers were ABI-incompatible with the custom rootfs. Maintained Buildroot-based build configs and managed kernel and library dependencies with CMake. Packaged the full development environment as a .deb to handle dependency resolution automatically, cutting new engineer onboarding from half a day to a few minutes.',
      'Linux Userspace & Service Management: Wrote initd scripts for AMLOGIC and CVITEK platforms and systemd service files for STM32-based boards, covering pre-flight checks, firmware startup and shutdown, watchdog handling, web server management, and update-checker services.',
      'CI/CD & Release Automation: Designed GitLab CI pipelines covering firmware builds, unit tests, OTA firmware image delivery, and regression tests. Cut release cycle time by 40% and eliminated all manual deployment steps.',
      'Heap Profiling & Leak Detection: Built a custom memory sampling layer in C that recorded heap consumption at regular intervals throughout firmware operation. Used the output to identify allocation patterns that did not match expected growth curves, isolating persistent leaks that were causing field failures and would not have been visible through standard crash analysis alone.',
      'Reliability Recovery: Diagnosed the root causes behind sub-95% fleet uptime - persistent heap leaks confirmed through memory sampling, OOM events traced through dmesg and kernel panic logs, and IPC race conditions found through GDB and Valgrind. Built a software watchdog thread that monitors system health, dumps variable state on anomaly detection, and triggers a controlled reboot to protect hardware. Brought fleet uptime from sub-95% to 99.9%.',
      'Fleet Telemetry & Benchmarking: Built a post-deployment telemetry system tracking hashrate, power draw, board temperatures, fan RPMs, efficiency ratios, and restart history across the fleet. Used this data to benchmark our firmware performance against competing OEM stacks running identical hardware, identified efficiency gaps in thermal management and load distribution, drove firmware-level optimizations to close those gaps, and validated improvements against fleet-wide telemetry.',
    ],
  },
  {
    company: 'Probots Techno Solutions',
    location: 'Bengaluru, India',
    role: 'Embedded Systems Engineer',
    period: 'June 2023 – Mar 2024',
    highlights: [
      'STM32 Firmware Development: Developed and maintained firmware on STM32 targets covering UART, I2C, and PWM peripheral drivers, plus system service scripts for startup, shutdown, and watchdog handling.',
      'Board Bring-up & RTOS: Brought up a full ASIC firmware stack from scratch in 7 days - no hardware documentation, no vendor support, hard product deadline. Wrote I2C and UART drivers on ESP-IDF with FreeRTOS scheduling and delivered on time.',
      'Test Fixture Firmware & Fault Isolation: Wrote firmware for a pogo-pin Bed of Nails test fixture used in production validation. Implemented a Binary Search power-domain toggling strategy to isolate faulty chips at the board level, cutting manual debug time by 60%.',
      'Python Test Tooling: Built a multithreaded desktop app in Python (PyQt) to stream live sensor data via REST and TCP APIs for QA monitoring. Wrote a custom installation CLI alongside it, together reducing end-to-end testing time by 50%.',
    ],
  },
  {
    company: 'Probots Techno Solutions',
    location: 'Bengaluru, India',
    role: 'Embedded Systems Intern',
    period: 'Feb 2023 – Mar 2023',
    highlights: [
      'Firmware & Protocol Validation: Wrote CRC validation routines used in ASIC communication pipelines to verify data integrity on send and receive paths. Also wrote Python scripts to parse and log hardware-firmware communication data during PoC validation testing.',
    ],
  },
];

const ExperienceCard = ({ experience, index }) => {
  const isEven = index % 2 === 0;
  const animationVariant = isEven ? fadeInLeft : fadeInRight;

  return (
    <motion.div
      variants={animationVariant}
      initial="initial"
      whileInView="whileInView"
      viewport={{ once: true }}
      className="relative pl-8 pb-12 last:pb-0"
    >
      {/* Timeline line */}
      <div className="absolute left-0 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-primaryLight to-transparent" />

      {/* Timeline dot */}
      <div className="absolute left-0 top-2 w-3 h-3 -translate-x-1/2 rounded-full bg-primary ring-4 ring-primary/20" />

      <div className="card hover:border-primary/50">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 mb-4">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-2">
              <MdWork className="text-primary" />
              {experience.company}
            </h3>
            <p className="text-primary font-medium mt-1">{experience.role}</p>
          </div>
          <div className="flex flex-col md:items-end text-sm text-textSecondary">
            <span className="flex items-center gap-1">
              <MdCalendarToday className="text-primaryLight" />
              {experience.period}
            </span>
            <span className="flex items-center gap-1">
              <MdLocationOn className="text-primaryLight" />
              {experience.location}
            </span>
          </div>
        </div>

        {/* Highlights */}
        <ul className="space-y-3">
          {experience.highlights.map((highlight, idx) => {
            const colonIdx = highlight.indexOf(':');
            const [title, content] = colonIdx !== -1
              ? [highlight.slice(0, colonIdx), highlight.slice(colonIdx + 1)]
              : [null, highlight];

            return (
              <li key={idx} className="text-textSecondary flex gap-3">
                <span className="text-primary mt-1.5 flex-shrink-0">▸</span>
                <span className="leading-relaxed">
                  {title ? (
                    <>
                      <strong className="text-white">{title}:</strong>{content}
                    </>
                  ) : (
                    highlight
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
};

const Experience = () => {
  return (
    <section id="experience" className="py-24 px-6">
      <div className="max-w-4xl mx-auto">
        <motion.h2
          variants={fadeInUp}
          initial="initial"
          whileInView="whileInView"
          viewport={{ once: true }}
          className="section-header"
        >
          Experience
        </motion.h2>

        <div className="mt-8">
          {experiences.map((exp, index) => (
            <ExperienceCard key={index} experience={exp} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
